/**
 * Which sort and filter controls a column's header carries, and what they are currently set to. Shared by
 * both overview tables so the rules have one definition rather than one per table.
 */

import { isEqual } from 'lodash-es';

import {
    Cell,
    ColumnDef as SortableColumnDef,
    FieldType,
    QueryPartData,
    ShColumnFilterField,
    ShFacet,
    SortOptionsHelper
} from '@stylehaven/components';

import { Column } from '../entities/column.entity';
import { Comparable } from '../entities/misc/comparable.interface';
import { Trackable } from '../entities/misc/trackable.interface';

const TEXT_FILTERABLE_TYPES: FieldType[] = [FieldType.TextField, FieldType.SimpleField];

/**
 * The tables bind these from async streams, so they legitimately arrive `null` on the first change-detection
 * pass, before the streams emit. Dereferencing that null once emptied a whole overview, so the nullability is
 * declared rather than assumed away.
 */
type MaybeQueryParts = readonly QueryPartData[] | null | undefined;
type MaybeFieldKeys = readonly string[] | null | undefined;

/** A derived column — one assembled from several fields — is recognised by its key and sort field differing. */
function filtersOnItsOwnField<T extends Comparable & Trackable>(column: Column<T>): boolean {
    return column.columnDefinition.key === column.columnDefinition.sortField;
}

export function isColumnTextFilterable<T extends Comparable & Trackable>(column: Column<T>): boolean {
    return (
        !!column.fieldDefinition &&
        TEXT_FILTERABLE_TYPES.includes(column.fieldDefinition.Type) &&
        filtersOnItsOwnField(column)
    );
}

export function isColumnDateFilterable<T extends Comparable & Trackable>(column: Column<T>): boolean {
    return (
        !!column.fieldDefinition && column.fieldDefinition.Type === FieldType.DateField && filtersOnItsOwnField(column)
    );
}

/**
 * Enum and boolean columns share the same facet dropdown, so both are filterable only when the field has a
 * matching facet on the side panel (not every column does).
 */
export function isColumnFacetFilterable<T extends Comparable & Trackable>(
    column: Column<T>,
    enumFacetFieldKeys: MaybeFieldKeys
): boolean {
    const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;
    const type: FieldType | undefined = column.fieldDefinition?.Type;

    return (
        (type === FieldType.EnumField || type === FieldType.BooleanField) &&
        filtersOnItsOwnField(column) &&
        !!dottedKey &&
        !!enumFacetFieldKeys?.includes(dottedKey)
    );
}

/**
 * A column that renders more than one field declares its filter inputs outright, because none of the three
 * rules above can find them: it has no field definition of its own, and its key is not a real field.
 */
export function isColumnMultiTextFilterable<T extends Comparable & Trackable>(column: Column<T>): boolean {
    return column.columnDefinition.filterFields.length > 0;
}

/** Pairs each of a column's declared filter fields with the value currently committed for it. */
export function toColumnFilterFields<T extends Comparable & Trackable>(
    column: Column<T>,
    activeColumnFilters: MaybeQueryParts
): ShColumnFilterField[] {
    return column.columnDefinition.filterFields.map(filterField => ({
        field: filterField.field,
        label: filterField.label,
        value: activeColumnFilters?.find(queryPart => queryPart.field === filterField.field)?.values[0] ?? ''
    }));
}

/**
 * Keeps the last array handed to each column's filter popover and re-hands it while its contents are
 * unchanged. A template method returning a fresh array on every change-detection pass changes a binding's
 * identity on every pass, which development-mode change detection reports as `NG0100`.
 *
 * It is *not* what keeps a half-typed input intact — the popover's per-field buffers do that, since they
 * reset on their own field's committed value rather than on this array.
 */
export class ColumnFilterFieldsCache {
    private readonly byColumnKey = new Map<string, ShColumnFilterField[]>();

    get<T extends Comparable & Trackable>(
        column: Column<T>,
        activeColumnFilters: MaybeQueryParts
    ): ShColumnFilterField[] {
        const next: ShColumnFilterField[] = toColumnFilterFields(column, activeColumnFilters);
        const previous: ShColumnFilterField[] | undefined = this.byColumnKey.get(column.columnDefinition.key);

        if (previous && isEqual(previous, next)) {
            return previous;
        }

        this.byColumnKey.set(column.columnDefinition.key, next);

        return next;
    }
}

/** Whether the column gets a filter control at all — any one of the variants above. */
export function hasColumnFilter<T extends Comparable & Trackable>(
    column: Column<T>,
    enumFacetFieldKeys: MaybeFieldKeys
): boolean {
    return (
        isColumnMultiTextFilterable(column) ||
        isColumnTextFilterable(column) ||
        isColumnDateFilterable(column) ||
        isColumnFacetFilterable(column, enumFacetFieldKeys)
    );
}

export function getColumnFilterValue<T extends Comparable & Trackable>(
    column: Column<T>,
    activeColumnFilters: MaybeQueryParts
): string {
    return activeColumnFilters?.find(queryPart => queryPart.field === column.columnDefinition.key)?.values[0] ?? '';
}

export function getColumnEnumFacet<T extends Comparable & Trackable>(
    column: Column<T>,
    enumFacets: ReadonlyMap<string, ShFacet> | null | undefined
): ShFacet | null {
    const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;

    return dottedKey ? (enumFacets?.get(dottedKey) ?? null) : null;
}

export function getColumnSelectedEnumValues<T extends Comparable & Trackable>(
    column: Column<T>,
    activeFacetFilters: MaybeQueryParts
): string[] {
    const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;
    if (!dottedKey) {
        return [];
    }

    return activeFacetFilters?.find(queryPart => queryPart.field === dottedKey)?.values ?? [];
}

export function toSortColumnDef<T extends Comparable & Trackable>(column: Column<T>): SortableColumnDef<any, any> {
    return new SortableColumnDef({
        key: column.columnDefinition.sortField,
        translateLabel: column.columnDefinition.label,
        mapper: (): Cell => undefined as unknown as Cell,
        sortable: column.columnDefinition.sortable,
        sortType:
            column.columnDefinition.sortType ??
            (column.fieldDefinition ? SortOptionsHelper.getSortType(column.fieldDefinition.Type) : undefined)
    });
}
